import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import GUI from 'lil-gui';
import Reactor from './components/simulation/Reactor';
import useSimulation from './hooks/useSimulation';
import { useControlPanelGui } from './hooks/useControlPanelGui';
import { useViewSettingsGui } from './hooks/useViewSettingsGui';
import { useStyleSettingsGui } from './hooks/useStyleSettingsGui';
import { useChemistryGui } from './hooks/useChemistryGui';
import StatisticsDisplay from './components/statistics/StatisticsDisplay';
import StatisticsPanel from './components/ui/StatisticsPanel';
import SpeciesPanel from './components/ui/SpeciesPanel';
import ReactionsPanel from './components/ui/ReactionsPanel';
import ControlPanel from './components/ui/ControlPanel';
import SidebarLayout from './components/ui/SidebarLayout';
import useWakeLock from './hooks/useWakeLock';
import ActiveReactionsDisplay from './components/ui/ActiveReactionsDisplay';
import ReactionEditorPanel from './components/editor/ReactionEditorPanel';
import FloatingChartManager from './components/ui/FloatingChartManager';
import TopControlsOverlay from './components/ui/TopControlsOverlay';

import useSimulationStore from './store/simulationStore';
import { shallow } from 'zustand/shallow';

// Define the expected shape of the particle state array from the worker
type WorkerParticleState = {
  id: string;
  speciesName: string;
  isActive: boolean;
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#f50057',
    },
  },
});

const App: React.FC = () => {
  //console.log("[App] Component function START - should only run after AppLoader finishes loading");
  // Initialize simulation
  useSimulation();
  // Keep screen awake while app is open
  useWakeLock(true);

  // --- Load Default Preset on Startup --- 
  const loadPresetIntoActiveConfig = useSimulationStore(state => state.loadPresetIntoActiveConfig);
  const { speciesLength, reactionsLength } = useSimulationStore(state => ({
    speciesLength: state.availableSpecies.length,
    reactionsLength: state.availableReactions.length,
  }));
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    const loadInitialPreset = async () => {
      if (speciesLength > 0 && reactionsLength > 0 && !loadedOnceRef.current) {
        loadedOnceRef.current = true;
        try {
          await loadPresetIntoActiveConfig('o2_water_formation.json');
          const stateAfterLoad = useSimulationStore.getState();
          stateAfterLoad.resetSimulation();
        } catch (error) {
          console.error("[App Preset Load] Error loading default preset:", error);
          loadedOnceRef.current = false;
        }
      }

      if (speciesLength === 0 || reactionsLength === 0) {
        if (loadedOnceRef.current) {
          loadedOnceRef.current = false;
        }
      }
    };

    loadInitialPreset();
  }, [speciesLength, reactionsLength, loadPresetIntoActiveConfig]);

  // --- State for Worker Particle Data ---
  const [currentParticleStates, setCurrentParticleStates] = useState<WorkerParticleState[]>([]);

  // --- Callback for Worker to Update Particle State ---
  const handleStatesUpdate = useCallback((newStates: WorkerParticleState[]) => {
    setCurrentParticleStates(newStates);

    // Calculate species counts from particle states and update store
    const counts: { [speciesName: string]: number } = {};
    let totalActive = 0;
    for (const p of newStates) {
      if (p.isActive && p.speciesName) {
        counts[p.speciesName] = (counts[p.speciesName] || 0) + 1;
        totalActive++;
      }
    }

    // Update store with new counts (throttled by the worker's 60fps updates)
    useSimulationStore.setState({
      speciesCounts: counts,
      particleCount: totalActive,
    });
  }, []);

  // --- GUI Setup --- 
  const chemistryGuiRef = useRef<GUI | null>(null);
  const simViewGuiRef = useRef<GUI | null>(null);
  const guiContainerRef = useRef<HTMLDivElement>(null);
  const [gui, setGui] = useState<GUI | null>(null);
  const [guiInitialized, setGuiInitialized] = useState(false);

  // Get shader state and action from store - using 'as any' to bypass persistent TS error
  const activeVisShader = useSimulationStore((state) => state.activeVisShader);
  const setActiveVisShader = useSimulationStore((state) => (state as any).setActiveVisShader);

  // Get UI feature flags for component switching
  const { uiFeatures } = useSimulationStore(state => ({
    uiFeatures: state.uiFeatures
  }));

  // --- Effect to create/destroy GUI instances --- 
  useEffect(() => {
    let chemGui: GUI | null = null;
    let simViewGui: GUI | null = null;

    // Only create GUI instances if new controls are disabled
    if (!uiFeatures.useNewControls) {
      if (!chemistryGuiRef.current) {
        console.log('Creating Chemistry GUI instance');
        chemGui = new GUI({ title: 'Chemistry Settings', autoPlace: false });
        chemistryGuiRef.current = chemGui;
        chemGui.domElement.classList.add('chemistry-gui');
        document.body.appendChild(chemGui.domElement);
        chemGui.close(); // Start minimized
      }

      if (!simViewGuiRef.current) {
        console.log('Creating Sim/View GUI instance');
        simViewGui = new GUI({ title: 'Simulation & View', autoPlace: false });
        simViewGuiRef.current = simViewGui;
        simViewGui.domElement.classList.add('sim-view-gui');
        document.body.appendChild(simViewGui.domElement);
        simViewGui.close(); // Start minimized
      }
    } else {
      // If new controls are enabled, destroy existing GUI instances
      if (chemistryGuiRef.current) {
        console.log('Destroying Chemistry GUI instance (new controls enabled)');
        if (chemistryGuiRef.current.domElement.parentNode === document.body) {
          document.body.removeChild(chemistryGuiRef.current.domElement);
        }
        chemistryGuiRef.current.destroy();
        chemistryGuiRef.current = null;
      }
      if (simViewGuiRef.current) {
        console.log('Destroying Sim/View GUI instance (new controls enabled)');
        if (simViewGuiRef.current.domElement.parentNode === document.body) {
          document.body.removeChild(simViewGuiRef.current.domElement);
        }
        simViewGuiRef.current.destroy();
        simViewGuiRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (chemistryGuiRef.current) {
        console.log('Destroying Chemistry GUI instance');
        if (chemistryGuiRef.current.domElement.parentNode === document.body) {
          document.body.removeChild(chemistryGuiRef.current.domElement);
        }
        chemistryGuiRef.current.destroy();
        chemistryGuiRef.current = null;
      }
      if (simViewGuiRef.current) {
        console.log('Destroying Sim/View GUI instance');
        if (simViewGuiRef.current.domElement.parentNode === document.body) {
          document.body.removeChild(simViewGuiRef.current.domElement);
        }
        simViewGuiRef.current.destroy();
        simViewGuiRef.current = null;
      }
    };
  }, [uiFeatures.useNewControls]); // Re-run when feature flag changes

  // --- Populate the GUI instances using Hooks ---
  useChemistryGui(chemistryGuiRef.current);
  useControlPanelGui(simViewGuiRef.current);
  useViewSettingsGui(simViewGuiRef.current);
  useStyleSettingsGui(simViewGuiRef.current);

  // --- Effect to inject CSS for positioning --- 
  useEffect(() => {
    const styleId = 'lil-gui-position-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    styleElement.innerHTML = `
      .lil-gui.root.chemistry-gui {
        position: absolute!important;
        top: 10px!important; 
        left: 10px!important;  
        right: auto!important; 
        min-width: 350px !important;
        z-index: 1001!important; 
      }
      .lil-gui.root.sim-view-gui {
        position: absolute!important;
        top: 10px!important; 
        left: auto!important;  
        right: 10px!important; 
        z-index: 1001!important; 
      }
    `;
  }, []);

  // Handler for the temporary dropdown
  const handleShaderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (setActiveVisShader) { // Check if the action exists at runtime
      setActiveVisShader(event.target.value);
    } else {
      console.error("setActiveVisShader action is missing from the store!");
    }
  };

  // console.log(`[App Render] Passing ${currentParticleStates.length} particle states to Reactor.`);
  // If needed:
  // if (currentParticleStates.length > 0) console.log("[App Render] First state to Reactor:", currentParticleStates[0]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box component="div"
        sx={{
          width: '100vw',
          height: '100vh',
          position: 'relative',
          overflow: 'hidden',
        } as any}
      >
        {/* GUI Panel Placeholder */}
        <Box component="div" ref={guiContainerRef} sx={{ width: '300px', height: '100%', overflowY: 'auto', backgroundColor: '#f0f0f0', padding: '10px' } as any} >
          {/* Temporary Shader Selector */}
          <div style={{ marginBottom: '15px', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
            <label htmlFor="shader-select" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select Atom Shader (TEMP):
            </label>
            <select
              id="shader-select"
              value={activeVisShader}
              onChange={handleShaderChange}
              style={{ width: '100%', padding: '5px' }}
            >
              <option value="PBR_InstanceColor">PBR Instance Color</option>
              <option value="SwirlEffect">Swirl Effect</option>
              <option value="TestBlue">Test Blue</option>
            </select>
          </div>
          {/* Existing GUI elements or placeholder text */}
          {/* {guiInitialized ? 'GUI Initialized' : 'Initializing GUI...'} */}
        </Box>

        {/* Main Simulation Area */}
        <Box component="div" sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1
        }}>
          <Reactor onStatesUpdate={handleStatesUpdate} />
        </Box>

        {/* New Sidebar Layout (unified interface) */}
        {uiFeatures.useNewLayout ? (
          <SidebarLayout particleStates={currentParticleStates} />
        ) : (
          <>
            {/* Stats Panels (unrelated to lil-gui) */}
            {uiFeatures.useNewStatistics ? (
              <StatisticsPanel particleStates={currentParticleStates} />
            ) : (
              <StatisticsDisplay particleStates={currentParticleStates} />
            )}

            {/* Reactions Panel (new component) */}
            {uiFeatures.useNewReactions ? (
              <ReactionsPanel />
            ) : (
              <ActiveReactionsDisplay />
            )}

            {/* Species Panel (new component) */}
            {uiFeatures.useNewSpecies && <SpeciesPanel />}

            {/* Control Panel (new component) */}
            {uiFeatures.useNewControls && <ControlPanel />}
          </>
        )}

        {/* Reaction Editor Modal (conditionally rendered based on store state) */}
        <ReactionEditorPanel />

        {/* Floating Chart Windows - rendered at App level so they persist when sidebar closes */}
        <FloatingChartManager />

        {/* Top Controls Overlay - fade-on-hover at bottom center */}
        <TopControlsOverlay />



      </Box>
    </ThemeProvider>
  );
};

export default App;
